// src/pettyCash/index.js  →  dist/pettyCash.js  (globalName: PettyCash)
//
// v129.77 — 💵 Petty Cash Claims, the STAFF side (the ops manager's digitalisation
// ask; Matt's ruling 7 Aug 2026). One expense = one PC-<id> ticket, submitted
// as it happens with the bill photographed/attached. AM/HO approves in the
// admin app; approval mints the CODE the store quotes when it deducts the
// approved amount from till cash; "Cash taken" here settles the ticket.
//
// Money-ADJACENT (no pay path). Submit + settle are PIN-authed via DB
// (_withAuthRetry) and the Worker enforces the store-match gate: a store can
// only act on its OWN claims.
//
// Free variables from the index.html inline-globals scope (classic-script
// pattern): DB, currentStore, escapeHtml, showToast, localDateStr,
// getStoreCountry (src/helpers). All guarded where absence is survivable.

var _pcAtts = [];        // pending attachments for the form: {filename, content_type, data}
var _pcBusy = false;

// Known keys get a friendly emoji label; anything Head Office adds via the
// admin category editor (v129.80) shows capitalised as typed.
var PC_CATEGORY_LABELS = {
  vehicle: '🚗 Vehicle expenses',
  taxi: '🚕 Taxi',
  food: '🍔 Food expenses',
  travelling: '🚕 Travelling expenses',
  telephone: '📞 Telephone expenses',
  stationery: '✏️ Stationery',
  marketing: '📣 Marketing expenses',
  misc: '📦 Miscellaneous',
};
var PC_DEFAULT_CATS = ['vehicle', 'food', 'travelling', 'telephone', 'stationery', 'marketing', 'misc'];
var _pcCats = null;   // the configured list, learned from the claims response

function catLabel(key) {
  if (PC_CATEGORY_LABELS[key]) return PC_CATEGORY_LABELS[key];
  var s = String(key || '');
  return s.charAt(0).toUpperCase() + s.slice(1);
}
function catOptionsHtml() {
  return (_pcCats || PC_DEFAULT_CATS).map(function (k) {
    return '<option value="' + esc(k) + '">' + esc(catLabel(k)) + '</option>';   // v136.63 (war game, xss): escaped
  }).join('');
}

var PC_STATUS_CHIP = {
  submitted: { label: 'Waiting for approval', bg: 'var(--warn-bg)', fg: 'var(--warn-deep)' },
  returned:  { label: 'Returned — fix & resubmit', bg: 'var(--danger-bg)', fg: 'var(--danger-deep)' },   // legacy pilot rows only (v129.80 dropped Return)
  approved:  { label: 'APPROVED — take the cash', bg: 'var(--ok-bg)', fg: 'var(--ok-deep)' },
  withdrawn: { label: 'Withdrawn', bg: 'var(--learn-track)', fg: 'var(--text)' },   // v131.12 - the store pulled it back
  rejected:  { label: 'Declined', bg: 'var(--danger-bg)', fg: 'var(--danger-deep)' },
  settled:   { label: 'Settled ✓', bg: 'var(--learn-track)', fg: 'var(--text)' },
};

function pcStore() { return (typeof currentStore !== 'undefined' && currentStore) || ''; }

function pcCurrency() {
  try {
    var c = (typeof getStoreCountry === 'function') ? getStoreCountry(pcStore()) : '';
    return /qatar/i.test(String(c || '')) ? 'QAR' : 'AED';
  } catch (e) { return 'AED'; }
}

function pcToday() {
  try { if (typeof localDateStr === 'function') return localDateStr(new Date()); } catch (e) {}
  // Dubai is UTC+4 — shift then read UTC (never a naive toISOString on local time).
  return new Date(Date.now() + 4 * 3600 * 1000).toISOString().slice(0, 10);
}

// v135.1: English or Arabic for a confirm or alert box, which the page translator never sees.
function tr(s) { return (typeof I18N !== 'undefined' && I18N.t) ? I18N.t(s) : s; }
// v136.63 (war game, xss): the fallback escapes too, it never returns the raw text
function esc(s) { var t = String(s == null ? '' : s); return (typeof escapeHtml === 'function') ? escapeHtml(t) : t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;'); }
function toast(msg, kind) { if (typeof showToast === 'function') showToast(msg, kind); }

// ── Render ─────────────────────────────────────────────────────────────────────

function render() {
  var root = document.getElementById('pettyCashRoot');
  if (!root) return;
  var cur = pcCurrency();
  var catOpts = catOptionsHtml();
  root.innerHTML =
    '<div style="max-width:560px;margin:0 auto">' +
      // v129.137 — FULL ROLLOUT (Matt, 14 Aug). The v129.78 pilot banner is
      // gone: petty cash is now the process for every store, not a trial.
      '<div style="background:var(--card);border:1px solid var(--border,#dde3e8);border-radius:14px;padding:16px;margin-bottom:12px">' +
        '<div style="font-weight:900;font-size:15px;margin-bottom:2px">💵 Claim a petty cash expense</div>' +
        '<div style="font-size:12px;color:var(--tiny);margin-bottom:12px">One expense per claim, submitted as it happens. Attach the bill. Your area manager or Head Office approves it — the approval code is what you use to balance the till.</div>' +
        '<label style="font-size:12px;font-weight:700;display:block;margin-bottom:4px">Category</label>' +
        '<select id="pcCategory" style="width:100%;padding:10px;border:1px solid var(--border);border-radius:10px;font-size:14px;margin-bottom:10px">' + catOpts + '</select>' +
        '<div style="display:flex;gap:10px;margin-bottom:10px">' +
          '<div style="flex:1"><label style="font-size:12px;font-weight:700;display:block;margin-bottom:4px">Amount (' + cur + ')</label>' +
            '<input id="pcAmount" type="number" inputmode="decimal" min="0.25" step="0.25" placeholder="0.00" style="width:100%;padding:10px;border:1px solid var(--border);border-radius:10px;font-size:14px;box-sizing:border-box"></div>' +
          '<div style="flex:1"><label style="font-size:12px;font-weight:700;display:block;margin-bottom:4px">Date of expense</label>' +
            '<input id="pcDate" type="date" value="' + pcToday() + '" style="width:100%;padding:10px;border:1px solid var(--border);border-radius:10px;font-size:14px;box-sizing:border-box"></div>' +
        '</div>' +
        '<label style="font-size:12px;font-weight:700;display:block;margin-bottom:4px">What was it for?</label>' +
        '<textarea id="pcNarration" rows="2" maxlength="300" placeholder="e.g. Taxi to bank for change" style="width:100%;padding:10px;border:1px solid var(--border);border-radius:10px;font-size:14px;box-sizing:border-box;resize:vertical"></textarea>' +
        '<div style="margin:10px 0">' +
          '<input id="pcFile" type="file" accept="image/*,application/pdf" style="display:none" onchange="PettyCash.filePicked(this)">' +
          '<button onclick="document.getElementById(\'pcFile\').click()" style="border:1px dashed var(--border-strong);background:var(--surface-2);color:var(--text);font-weight:700;font-size:13px;padding:9px 14px;border-radius:10px;cursor:pointer">📎 Attach the bill (photo or PDF)</button>' +
          '<div id="pcAttList" style="margin-top:6px"></div>' +
        '</div>' +
        '<button id="pcSubmitBtn" onclick="PettyCash.submit()" style="width:100%;border:none;background:var(--elc-green,#009E3D);color:#fff;font-weight:900;font-size:15px;padding:12px;border-radius:12px;cursor:pointer">Submit claim</button>' +
        '<div id="pcFormStatus" style="font-size:12px;margin-top:8px;min-height:16px"></div>' +
      '</div>' +
      '<div style="font-weight:900;font-size:14px;margin:14px 2px 8px">' + esc(pcStore() || 'Your store') + ' — recent claims</div>' +
      '<div id="pcClaims"><div style="font-size:13px;color:var(--tiny);padding:12px 2px">Loading…</div></div>' +
    '</div>';
  renderAttList();
  loadClaims();
}

function renderAttList() {
  var el = document.getElementById('pcAttList');
  if (!el) return;
  el.innerHTML = _pcAtts.map(function (a, i) {
    return '<div style="display:flex;align-items:center;gap:8px;font-size:12px;padding:4px 0">' +
      '<span>' + (a.content_type === 'application/pdf' ? '📄' : '🖼️') + ' ' + esc(a.filename) + '</span>' +
      '<button onclick="PettyCash.removeAtt(' + i + ')" style="border:none;background:none;color:var(--danger);font-weight:800;cursor:pointer;font-size:12px">✕ remove</button>' +
    '</div>';
  }).join('');
}

function removeAtt(i) { _pcAtts.splice(i, 1); renderAttList(); }

function filePicked(input) {
  var file = input && input.files && input.files[0];
  if (input) input.value = '';
  if (!file) return;
  if (_pcAtts.length >= 5) { toast('Max 5 attachments per claim', 'error'); return; }
  if (file.type === 'application/pdf') {
    if (file.size > 2 * 1024 * 1024) { toast('PDF too large — keep it under 2 MB (or photograph the bill instead)', 'error'); return; }
    var reader = new FileReader();
    reader.onload = function () {
      _pcAtts.push({ filename: file.name || 'bill.pdf', content_type: 'application/pdf', data: String(reader.result || '') });
      renderAttList();
    };
    reader.onerror = function () { toast('Could not read that PDF — try again', 'error'); };
    reader.readAsDataURL(file);
    return;
  }
  // Photos ride the chat compressor pattern: canvas, max 1280px, stepped JPEG.
  compressBillImage(file, function (dataUrl) {
    if (!dataUrl) { toast('Could not use that photo — try a different one', 'error'); return; }
    _pcAtts.push({ filename: file.name || 'bill.jpg', content_type: 'image/jpeg', data: dataUrl });
    renderAttList();
  });
}

// Client-side compression (the chat photoPicked pattern — a 6MB camera shot
// becomes a readable ~100-250KB bill; the Worker hard-caps too).
function compressBillImage(file, cb) {
  var img = new Image();
  var url = URL.createObjectURL(file);
  img.onload = function () {
    try {
      var scale = Math.min(1, 1280 / Math.max(img.width, img.height));
      var canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(img.width * scale));
      canvas.height = Math.max(1, Math.round(img.height * scale));
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      var out = null;
      [0.72, 0.55, 0.4].some(function (q) {
        var d = canvas.toDataURL('image/jpeg', q);
        out = d;
        return d.length <= 400000;
      });
      URL.revokeObjectURL(url);
      cb(out);   // bills must stay LEGIBLE — the lowest-quality pass always ships; server cap is generous
    } catch (e) { URL.revokeObjectURL(url); cb(null); }
  };
  img.onerror = function () { URL.revokeObjectURL(url); cb(null); };
  img.src = url;
}

// ── Submit ────────────────────────────────────────────────────────────────────

async function submit() {
  if (_pcBusy) return;
  var status = document.getElementById('pcFormStatus');
  var btn = document.getElementById('pcSubmitBtn');
  var category = (document.getElementById('pcCategory') || {}).value || '';
  var amount = parseFloat((document.getElementById('pcAmount') || {}).value || '');
  var expense_date = (document.getElementById('pcDate') || {}).value || '';
  var narration = ((document.getElementById('pcNarration') || {}).value || '').trim();
  var fail = function (m) { if (status) { status.style.color = 'var(--danger)'; status.textContent = m; } };
  if (!pcStore()) return fail('No store selected — reload the app.');
  if (!isFinite(amount) || amount <= 0) return fail('Enter the amount.');
  if (!narration) return fail('Say what the expense was for.');
  if (!expense_date) return fail('Pick the date of the expense.');
  if (!_pcAtts.length && !window.confirm(tr('No bill attached — submit anyway? Claims without a bill are usually returned.'))) return;
  _pcBusy = true;
  if (btn) btn.disabled = true;
  if (status) { status.style.color = 'var(--tiny)'; status.textContent = 'Submitting…'; }
  try {
    var res = await DB.pettyCash.submit({
      store: pcStore(), category: category, narration: narration,
      amount: amount, expense_date: expense_date, attachments: _pcAtts,
    });
    _pcAtts = [];
    toast('Claim ' + (res.ticket || '') + ' submitted for approval');
    render();
  } catch (e) {
    fail((e && e.message) || 'Could not submit — try again.');
  } finally {
    _pcBusy = false;
    if (btn) btn.disabled = false;
  }
}

// ── The claims list ───────────────────────────────────────────────────────────

async function loadClaims() {
  var el = document.getElementById('pcClaims');
  if (!el || !pcStore()) return;
  try {
    var res = await DB.pettyCash.list(pcStore());
    // v129.80: the configured category list rides the claims response — if HO
    // has customised it, refresh the form's dropdown in place (keep selection).
    if (res && Array.isArray(res.categories) && res.categories.length) {
      var changed = JSON.stringify(res.categories) !== JSON.stringify(_pcCats || PC_DEFAULT_CATS);
      _pcCats = res.categories;
      var sel = document.getElementById('pcCategory');
      if (sel && changed) {
        var keep = sel.value;
        sel.innerHTML = catOptionsHtml();
        if (_pcCats.indexOf(keep) !== -1) sel.value = keep;
      }
    }
    var claims = (res && res.claims) || [];
    if (!claims.length) {
      el.innerHTML = '<div style="font-size:13px;color:var(--tiny);padding:12px 2px">No claims yet.</div>';
      return;
    }
    el.innerHTML = claims.map(claimCardHtml).join('');
  } catch (e) {
    el.innerHTML = '<div style="font-size:13px;color:var(--danger);padding:12px 2px">' + esc((e && e.message) || 'Could not load claims') + '</div>';
  }
}

function claimCardHtml(c) {
  var chip = PC_STATUS_CHIP[c.status] || { label: c.status, bg: 'var(--learn-track)', fg: 'var(--text)' };
  var cat = catLabel(c.category);
  var h = '<div style="background:var(--card);border:1px solid var(--border,#dde3e8);border-radius:14px;padding:14px;margin-bottom:10px">' +
    '<div style="display:flex;justify-content:space-between;align-items:flex-start;gap:8px">' +
      '<div style="font-weight:900;font-size:14px">' + esc(c.ticket) + ' · ' + esc(c.currency) + ' ' + esc(Number(c.amount).toFixed(2)) + '</div>' +
      '<span style="font-size:11px;font-weight:800;padding:3px 9px;border-radius:99px;background:' + chip.bg + ';color:' + chip.fg + ';white-space:nowrap">' + chip.label + '</span>' +
    '</div>' +
    '<div style="font-size:12px;color:var(--tiny);margin-top:3px">' + esc(cat) + ' · ' + esc(c.expense_date) + (c.attachments ? ' · 📎 ' + c.attachments : '') + '</div>' +
    '<div translate="no" style="font-size:13px;margin-top:5px">' + esc(c.narration) + '</div>';
  // v131.12 (Khawaneej Mall) - a claim still WAITING can be fixed by the
  // store: add the forgotten bill, or withdraw it entirely and re-raise.
  // Neither touches a decided claim - the buttons only exist while waiting.
  if (c.status === 'submitted' || c.status === 'returned') {
    h += '<div style="display:flex;gap:8px;margin-top:10px">' +
      '<button onclick="PettyCash.addBill(' + c.id + ')" style="flex:1;border:1px dashed var(--border-strong);background:var(--surface-2);color:var(--text);font-weight:700;font-size:12.5px;padding:9px;border-radius:10px;cursor:pointer">📎 Add the bill</button>' +
      '<button onclick="PettyCash.withdraw(' + c.id + ')" style="flex:1;border:1px solid var(--learn-bad-line);background:var(--danger-bg);color:var(--danger-deep);font-weight:700;font-size:12.5px;padding:9px;border-radius:10px;cursor:pointer">Withdraw claim</button>' +
      '</div>';
  }
  if (c.status === 'approved' && c.approval_code) {
    h += '<div style="margin-top:10px;background:var(--ok-bg);border:2px dashed #059669;border-radius:12px;padding:10px;text-align:center">' +
      '<div style="font-size:11px;font-weight:700;color:var(--ok-deep)">APPROVAL CODE — quote this when balancing the till</div>' +
      '<div style="font-size:22px;font-weight:900;letter-spacing:2px;color:var(--ok-deep);font-family:monospace">' + esc(c.approval_code) + '</div>' +
      (c.approved_by ? '<div style="font-size:11px;color:var(--ok-deep);margin-top:2px">Approved by ' + esc(c.approved_by) + '</div>' : '') +
      '</div>' +
      '<button onclick="PettyCash.settle(' + c.id + ')" style="width:100%;margin-top:8px;border:none;background:#065f46;color:#fff;font-weight:800;font-size:13px;padding:10px;border-radius:10px;cursor:pointer">✓ Cash taken from till — mark settled</button>';
  }
  if (c.status === 'settled' && c.approval_code) {
    h += '<div style="font-size:12px;color:var(--tiny);margin-top:6px">Code ' + esc(c.approval_code) + (c.approved_by ? ' · approved by ' + esc(c.approved_by) : '') + '</div>';
  }
  if ((c.status === 'returned' || c.status === 'rejected') && c.decision_comment) {
    h += '<div style="margin-top:8px;background:var(--danger-bg);border-radius:10px;padding:8px;font-size:12px;color:var(--danger-deep)"><b>' +
      (c.status === 'returned' ? 'Returned' : 'Rejected') + ':</b> <span translate="no">' + esc(c.decision_comment) + '</span></div>';
  }
  return h + '</div>';
}

async function settle(id) {
  if (!window.confirm(tr('Confirm: the approved cash for this claim has been taken from the till? This closes the ticket.'))) return;
  try {
    await DB.pettyCash.settle(id);
    toast('Claim settled ✓');
    loadClaims();
  } catch (e) {
    toast((e && e.message) || 'Could not settle — try again', 'error');
  }
}

// ── v131.12 — fix a claim that is still waiting (Khawaneej Mall) ──────
// addBill reuses the SAME compression pipeline as the form (a 6MB camera
// shot becomes a ~250KB bill), then posts straight to the claim.
var _lateBillClaimId = null;

function addBill(id) {
  _lateBillClaimId = id;
  var input = document.getElementById('pcLateFile');
  if (!input) {
    input = document.createElement('input');
    input.type = 'file';
    input.id = 'pcLateFile';
    input.accept = 'image/*,application/pdf';
    input.style.display = 'none';
    input.onchange = function () { lateFilePicked(input); };
    document.body.appendChild(input);
  }
  input.click();
}

function lateFilePicked(input) {
  var file = input && input.files && input.files[0];
  if (input) input.value = '';
  var id = _lateBillClaimId;
  if (!file || !id) return;
  function send(att) {
    DB.pettyCash.attach(id, [att]).then(function () {
      toast('Bill added to PC-' + id + ' ✓');
      loadClaims();
    }).catch(function (e) {
      toast((e && e.message) || 'Could not add the bill — try again', 'error');
    });
  }
  if (file.type === 'application/pdf') {
    if (file.size > 2 * 1024 * 1024) { toast('PDF too large — keep it under 2 MB (or photograph the bill instead)', 'error'); return; }
    var reader = new FileReader();
    reader.onload = function () { send({ filename: file.name || 'bill.pdf', content_type: 'application/pdf', data: String(reader.result || '') }); };
    reader.onerror = function () { toast('Could not read that PDF — try again', 'error'); };
    reader.readAsDataURL(file);
    return;
  }
  compressBillImage(file, function (dataUrl) {
    if (!dataUrl) { toast('Could not use that photo — try a different one', 'error'); return; }
    send({ filename: file.name || 'bill.jpg', content_type: 'image/jpeg', data: dataUrl });
  });
}

async function withdraw(id) {
  // Plain words about what this does and does not do: the record stays,
  // it just stops waiting for approval. No money has moved at this stage.
  if (!window.confirm(tr('Withdraw claim PC-' + id + '? It leaves the approval queue and stays on your list as Withdrawn. If you still need the expense, raise a fresh claim with the bill attached.'))) return;
  try {
    await DB.pettyCash.withdraw(id);
    toast('Claim PC-' + id + ' withdrawn');
    loadClaims();
  } catch (e) {
    toast((e && e.message) || 'Could not withdraw — try again', 'error');
  }
}

export { render, submit, settle, filePicked, removeAtt, loadClaims, addBill, lateFilePicked, withdraw };
